export type CarouselFocusRing = 'dark' | 'light';

// The hex values of --in-color-nero-non-nero and --in-color-bianco, the two
// colours the ring is drawn with.
const DARK_RING_HEX = '1b212b';
const LIGHT_RING_HEX = 'ffffff';

function channelLuminance(channel: number): number {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number | null {
    if (!/^[0-9a-f]{6}$/i.test(hex)) {
        return null;
    }
    const [red, green, blue] = [0, 2, 4].map((start) =>
        channelLuminance(parseInt(hex.slice(start, start + 2), 16)),
    );
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

export function contrastRatio(first: string, second: string): number | null {
    const a = relativeLuminance(first);
    const b = relativeLuminance(second);
    if (a === null || b === null) {
        return null;
    }
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export function carouselFocusRing(
    backgroundHex: string,
): CarouselFocusRing | null {
    const dark = contrastRatio(backgroundHex, DARK_RING_HEX);
    const light = contrastRatio(backgroundHex, LIGHT_RING_HEX);
    if (dark === null || light === null) {
        return null;
    }
    return dark >= light ? 'dark' : 'light';
}

const MIN_TEXT_CONTRAST = 4.5;

export function readableTextColor(
    preferred: string,
    background: string,
): string {
    const ratio = contrastRatio(preferred, background);
    if (ratio === null || ratio >= MIN_TEXT_CONTRAST) {
        return preferred;
    }
    return carouselFocusRing(background) === 'light'
        ? LIGHT_RING_HEX
        : DARK_RING_HEX;
}
